// ════════════════════════════════════════════════════════════════════
// TERROR — THE SOUNDTRACK
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-08-19: *"I made a quick loop for the plaza, minerium, and
// the shatterfield how do I upload them so we can hear them"*
//
// ── ⭐ HOW TO UPLOAD THEM. Three steps and one of them is a filename ──
//
//   1. NAME THEM EXACTLY THIS. The code already points at these:
//
//          bgm_plaza.mp3          the hub, and every room off it
//          bgm_minerium.mp3       the world map
//          bgm_shatterfield.mp3   the duel
//
//   2. Drop them in  mass_transfer/Assets/terror-assets/  — the same
//      flat folder the card art lives in. Nothing else to change.
//
//   3. Deploy that folder to terror-assets.pages.dev with Wrangler,
//      exactly the way the cards go up.
//
//   That is it. No code edit. Refresh and the Plaza has music.
//
// ── MP3, NOT WAV. He asked which is smaller; it is not close ─────────
// WAV is uncompressed PCM — about 10 MB per stereo minute. MP3 at
// 160 kbps is about 1.2 MB per minute and is transparent for a
// background loop nobody is A/B-ing against a master. A 90-second
// theme is ~110 MB of WAV or ~1.8 MB of MP3, and the second number is
// the one a phone on a bad connection has to pull down before the
// Plaza is allowed to make a sound. Stereo, 44.1 kHz, 160-192 kbps.
//
// ── ⚠ THE GAP AT THE LOOP POINT, AND WHY IT IS NOT YOUR EDIT ─────────
// If a loop clicks or breathes at the seam, the export is probably
// fine — MP3 encoders ADD SILENT PADDING to the front and back of
// every file, because the format works in fixed-size frames and your
// audio almost never ends on a frame boundary. Chrome and Firefox read
// the LAME gapless tag and trim it; Safari is less reliable.
//
// So do NOT re-cut the loop first. Set `loopStart` / `loopEnd` on the
// track's row below (seconds, floats) and the trim happens at PLAYBACK
// against the decoded buffer, where the padding actually lives. That
// is a two-number fix in this file versus an afternoon in an editor
// chasing a gap that is not in the audio.
//
// ── WHY WEB AUDIO AND NOT `new Audio()` ──────────────────────────────
// `<audio loop>` re-seeks to 0 at the end of the file, which means it
// loops the padding too and gaps audibly on every platform. An
// AudioBufferSourceNode with `loop = true` is sample-accurate, honours
// loopStart/loopEnd, and gives a real gain node per track so screens
// can CROSSFADE instead of cutting. There is a plain-<audio> fallback
// at the bottom for a browser with no AudioContext; it cuts rather
// than fades and it cannot trim, and that is an acceptable floor.
//
// ── ⚠ THE FILES DO NOT EXIST YET, AND THE GAME MUST NOT CARE ─────────
// Nothing here is allowed to be load-bearing. A 404 marks that track
// dead ONCE, warns ONCE, and is never fetched again — because the
// asset domain is a Pages project and a miss on this CDN can answer
// 200 with an HTML document (see ie-cdn-assets: the app domain serves
// index.html for every path). decodeAudioData will reject that, which
// is exactly the same outcome as a 404 and is handled by the same
// branch. Silence is the correct behaviour for a game whose music has
// not shipped yet, and it must stay correct on the day one of the
// three files is missing and the other two are not.
//
// ── ⭐ 2026-09-30 — THE SIX FINAL SONGS, THE OVERRIDES, THE PLAYER ────
// caP'n: login/boot song, Sweet Treats for V'yen (and V'ael as a
// placeholder), a music icon with pause / play / next / back / mute.
//
//   FILES (all in Assets/terror-assets/, deploy with wrangler):
//     bgm_login_menu.mp3  bgm_plaza.mp3  bgm_overworld_1.mp3
//     bgm_shatterfield.mp3  bgm_affinity_room.mp3  bgm_sweet_treats.mp3
//
//   WHO PLAYS WHEN — three layers, highest wins:
//     1. MANUAL   the player pressed next/back. caP'n's ruling: it
//                 sticks for the rest of the session — no default
//                 button, rooms stop overriding it, a refresh is the
//                 only way back to the game's own picks.
//     2. OVERRIDE a mounted component said "I own the music right now":
//                 useMusicOverride("login") in Plaza until sign-in,
//                 NpcStage for V'yen/V'ael (KEEPER_TRACK). Stackable;
//                 the newest mounted wins; unmount restores the rest.
//     3. SCREEN   SCREEN_TRACK, exactly as before.
//
//   The boot sequence is screen "menu", so it plays the login song, and
//   Plaza holds the same override until a name exists — one continuous
//   song from boot until you are actually in. Same id = no restart.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { CDN } from "./CDN";

// ── THE TABLE ─────────────────────────────────────────────────
// `gain` is the track's own level, 0..1, so a loud master and a
// quiet one sit right against each other without touching the
// player's volume. `loopStart`/`loopEnd` are the padding trim
// described above — leave them null until a seam actually clicks.
export const TRACKS = {
  login: {
    title: "Login Menu",
    file: "bgm_login_menu.mp3",
    gain: 0.45,
    loopStart: null,
    loopEnd: null,
  },
  plaza: {
    title: "Plaza",
    file: "bgm_plaza.mp3",
    gain: 0.45,
    loopStart: null,
    loopEnd: null,
  },
  // Overworld 1 = the world map (Minerium 1). Was `minerium` /
  // bgm_minerium.mp3 before the final song landed; renamed to match
  // caP'n's own song title.
  overworld_1: {
    title: "Overworld 1",
    file: "bgm_overworld_1.mp3",
    gain: 0.42,
    loopStart: null,
    loopEnd: null,
  },
  shatterfield: {
    title: "Shatterfield",
    file: "bgm_shatterfield.mp3",
    // The duel is the one screen with its own sound design coming
    // later (strikes, the draw pulse). Its bed sits lower on purpose
    // so there is room above it.
    gain: 0.36,
    loopStart: null,
    loopEnd: null,
  },
  affinity: {
    title: "Affinity Room",
    file: "bgm_affinity_room.mp3",
    gain: 0.45,
    loopStart: null,
    loopEnd: null,
  },
  sweet_treats: {
    title: "Sweet Treats",
    file: "bgm_sweet_treats.mp3",
    gain: 0.45,
    loopStart: null,
    loopEnd: null,
  },
};

// The order next/back walks. Dial: reorder freely, ids only.
export const PLAYLIST = ["login", "plaza", "overworld_1", "shatterfield", "affinity", "sweet_treats"];

// Who owns the music while their dialogue window is open (NpcStage reads
// this). V'ael rides Sweet Treats as a placeholder until she has her own.
// Willy is deliberately absent: no row = the hub keeps playing.
export const KEEPER_TRACK = {
  vyen: "sweet_treats",
  vael: "sweet_treats",
};

// ── SCREEN → TRACK ────────────────────────────────────────────
// The hub theme covers the hub AND every room you reach from it.
// That is a deliberate call, not laziness: three tracks exist, and
// re-cueing the same loop from zero every time somebody steps into
// the Forge for four seconds is worse than continuous music. A room
// gets its own row here the day it gets its own theme.
//
// SILENT ON PURPOSE:
//   dungeon1  — at REST by decree, no route in
//   pierre    — another title's room; its own soundtrack, not ours
//   (anything unlisted) — silence, which is also what a brand-new
//   screen gets before anybody has thought about it. Correct default.
export const SCREEN_TRACK = {
  // The boot sequence IS screen "menu" — it plays the login song, and
  // Plaza keeps it going (useMusicOverride) until the player is in.
  menu:        "login",

  plaza:       "plaza",
  workshop:    "plaza",
  library:     "plaza",
  // The Affinity room (FactionHall) has its own song now.
  faction:     "affinity",
  forge:       "plaza",
  shop:        "plaza",
  collection:  "plaza",
  howtoplay:   "plaza",
  deckbuilder: "plaza",
  rulebook:    "plaza",
  storyreward: "plaza",
  tradepost:   "plaza",   // caP'n 2026-09-30: were silent
  fauxdex:     "plaza",

  worldmap:    "overworld_1",

  game:        "shatterfield",
};

export const trackForScreen = (screen) => SCREEN_TRACK[screen] || null;

// ── PLAYER SETTINGS ───────────────────────────────────────────
// localStorage and NOT the Firebase profile, deliberately. Volume is
// a property of THIS DEVICE — a player on headphones at night and the
// same player on a laptop speaker want different numbers, and syncing
// it across their devices would be a bug that looks like a feature.
// It is also the one setting that must survive being set before sign-in.
const LS_KEY = "terror_audio";
const DEFAULTS = { muted: false, volume: 0.7 };

function readSettings() {
  if (typeof localStorage === "undefined") return { ...DEFAULTS };
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    return {
      muted: typeof v.muted === "boolean" ? v.muted : DEFAULTS.muted,
      volume: typeof v.volume === "number" ? Math.min(1, Math.max(0, v.volume)) : DEFAULTS.volume,
    };
  } catch { return { ...DEFAULTS }; }
}

function writeSettings(s) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch { /* private mode */ }
}

let settings = readSettings();
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(settings));

export const getAudioSettings = () => settings;

export function setMuted(muted) {
  settings = { ...settings, muted: !!muted };
  writeSettings(settings);
  applyMaster();
  notify();
}

export function setVolume(volume) {
  settings = { ...settings, volume: Math.min(1, Math.max(0, volume)) };
  writeSettings(settings);
  applyMaster();
  notify();
}

export const toggleMuted = () => setMuted(!settings.muted);

/** Subscribe a component to mute/volume. Returns the live settings. */
export function useAudioSettings() {
  const [s, setS] = useState(settings);
  useEffect(() => {
    const fn = (next) => setS(next);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  return s;
}

// ── THE ENGINE ────────────────────────────────────────────────
const FADE = 0.9;              // seconds, both directions
let ctx = null;                // AudioContext, made on first gesture
let master = null;             // master GainNode
let current = null;            // { id, src, gain }
let wanted = null;             // the track the app has asked for
const buffers = new Map();     // id -> AudioBuffer
const dead = new Set();        // id -> never fetch again
const inflight = new Map();    // id -> Promise

function ensureCtx() {
  if (ctx) return ctx;
  const AC = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  try {
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = settings.muted ? 0 : settings.volume;
    master.connect(ctx.destination);
  } catch { ctx = null; }
  return ctx;
}

function applyMaster() {
  if (!master || !ctx) return;
  const target = settings.muted ? 0 : settings.volume;
  try {
    master.gain.cancelScheduledValues(ctx.currentTime);
    master.gain.setTargetAtTime(target, ctx.currentTime, 0.08);
  } catch { /* a closed context is not an error worth surfacing */ }
}

async function load(id) {
  if (buffers.has(id)) return buffers.get(id);
  if (dead.has(id)) return null;
  if (inflight.has(id)) return inflight.get(id);
  const track = TRACKS[id];
  if (!track || !ensureCtx()) return null;

  const p = (async () => {
    try {
      const res = await fetch(`${CDN}/${track.file}`, { cache: "force-cache" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const bytes = await res.arrayBuffer();
      // decodeAudioData is what catches the "200 with an HTML body"
      // case as well as a genuine 404 — an index.html is not audio and
      // this rejects on it. One branch, both failures.
      const buf = await ctx.decodeAudioData(bytes);
      buffers.set(id, buf);
      return buf;
    } catch (e) {
      dead.add(id);
      // ONE warn, ever. A missing soundtrack is the expected state
      // until the files ship and it must not fill the console that
      // the live-QA loop reads for real errors.
      console.info(`[soundtrack] "${track.file}" not available yet — staying silent.`);
      return null;
    } finally {
      inflight.delete(id);
    }
  })();
  inflight.set(id, p);
  return p;
}

function stop(node, at) {
  if (!node) return;
  try {
    node.gain.gain.cancelScheduledValues(at);
    node.gain.gain.setValueAtTime(node.gain.gain.value, at);
    node.gain.gain.linearRampToValueAtTime(0, at + FADE);
    node.src.stop(at + FADE + 0.05);
  } catch { /* already stopped */ }
}

async function play(id) {
  wanted = id;
  if (!id) { const c = current; current = null; if (c && ctx) stop(c, ctx.currentTime); return; }
  if (current && current.id === id) return;
  if (!ensureCtx()) return;

  const buf = await load(id);
  // The screen may have changed again while the fetch was in the air.
  // `wanted` is the truth; `id` is only what we set out to do.
  if (!buf || wanted !== id) return;
  if (current && current.id === id) return;

  const track = TRACKS[id];
  const now = ctx.currentTime;

  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  // The padding trim. Guarded so a bad pair of numbers cannot produce
  // a zero-length loop, which browsers handle by playing nothing at
  // all — a silent bug that looks exactly like a missing file.
  const ls = Number(track.loopStart);
  const le = Number(track.loopEnd);
  if (Number.isFinite(ls) && Number.isFinite(le) && le > ls && le <= buf.duration) {
    src.loopStart = ls;
    src.loopEnd = le;
  }

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(track.gain, now + FADE);
  src.connect(gain).connect(master);
  src.start(0, Number.isFinite(ls) ? ls : 0);

  if (current) stop(current, now);
  current = { id, src, gain };
}

// ── WHO DECIDES WHAT PLAYS (manual > override > screen) ───────
let screenTrack = null;        // from useSoundtrack(screen)
let overrides = [];            // [{ id }] newest last
let manual = null;             // the player's next/back pick
let lastContext = null;        // the song the game itself wants
let paused = false;            // the player's pause (not persisted: a
                               // pause is for now, mute is for the device)
const playerListeners = new Set();
const notifyPlayer = () => playerListeners.forEach((fn) => fn());

const contextTrack = () =>
  (overrides.length ? overrides[overrides.length - 1].id : screenTrack) || null;

function reevaluate() {
  const c = contextTrack();
  lastContext = c;
  // A manual pick is final: once the player touches the radio the rooms
  // no longer override it (caP'n, 2026-09-30). Refresh = back to default.
  play(manual || c);
  notifyPlayer();
}

/** While mounted, `id` owns the music (null/undefined = no claim). */
export function useMusicOverride(id) {
  useEffect(() => {
    if (!id || !TRACKS[id]) return undefined;
    const token = { id };
    overrides.push(token);
    reevaluate();
    return () => {
      overrides = overrides.filter((t) => t !== token);
      reevaluate();
    };
  }, [id]);
}

// ── THE PLAYER'S CONTROLS ─────────────────────────────────────
export function pauseMusic() {
  paused = true;
  if (ctx && ctx.state === "running") ctx.suspend().catch(() => {});
  notifyPlayer();
}

export function resumeMusic() {
  paused = false;
  if (ensureCtx() && ctx.state === "suspended") ctx.resume().catch(() => {});
  // Nothing ever started (first gesture was this click): start it now.
  if (wanted && !current) play(wanted);
  notifyPlayer();
}

export const togglePaused = () => (paused ? resumeMusic() : pauseMusic());

// dir = +1 next, -1 back. Skips songs already known to be missing so a
// single undeployed file never turns the button into a dead press.
function step(dir) {
  const n = PLAYLIST.length;
  const here = PLAYLIST.indexOf(wanted || lastContext);
  let i = here < 0 ? (dir > 0 ? -1 : 0) : here;
  for (let k = 0; k < n; k++) {
    i = (i + dir + n) % n;
    if (!dead.has(PLAYLIST[i])) { manual = PLAYLIST[i]; break; }
  }
  if (paused) resumeMusic();   // pressing next/back means "play me something"
  reevaluate();
}
export const nextTrack = () => step(1);
export const prevTrack = () => step(-1);

/** Live player state for the UI: what is playing, paused, muted. */
export function usePlayerState() {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    playerListeners.add(fn);
    listeners.add(fn);          // mute / volume changes too
    return () => { playerListeners.delete(fn); listeners.delete(fn); };
  }, []);
  const id = wanted;
  return {
    id,
    title: id && TRACKS[id] ? TRACKS[id].title : null,
    paused,
    muted: settings.muted,
  };
}

// ── THE GESTURE UNLOCK ────────────────────────────────────────
// Every browser refuses to make noise before the player has touched
// the page, and a suspended AudioContext is SILENT rather than an
// error — so without this, everything above works perfectly and
// nothing is audible, which is the worst kind of bug to chase.
// Listens once, on the first gesture of any kind, then removes itself.
let unlocked = false;
function unlock() {
  if (unlocked) return;
  unlocked = true;
  const c = ensureCtx();
  if (c && c.state === "suspended" && !paused) c.resume().catch(() => {});
  if (wanted) play(wanted);
  ["pointerdown", "keydown", "touchstart"].forEach((e) =>
    window.removeEventListener(e, unlock)
  );
}

function armUnlock() {
  if (typeof window === "undefined" || unlocked) return;
  ["pointerdown", "keydown", "touchstart"].forEach((e) =>
    window.addEventListener(e, unlock, { passive: true })
  );
}

// ══════════════════════════════════════════════════════════════
// useSoundtrack(screen) — THE ONE App ACTUALLY USES
// ══════════════════════════════════════════════════════════════
// App.js is a long ladder of `if (screen === "x") return (...)`, so a
// <Soundtrack> ELEMENT would have to be pasted into every branch —
// fifteen mount points, each of which is a chance for two to be live
// at once and play two loops over each other. That gets diagnosed as
// "the music is doubled" long before anybody counts the mounts.
//
// A hook at the top of the component body runs on every render no
// matter which branch returns, so there is exactly one of it by
// construction. This is the mount. The component below is for any
// future screen that owns its own audio outside App's ladder.
export function useSoundtrack(screen, enabled = true) {
  useEffect(() => { armUnlock(); }, []);
  useEffect(() => {
    screenTrack = enabled ? trackForScreen(screen) : null;
    reevaluate();
  }, [screen, enabled]);
  useEffect(() => () => { screenTrack = null; manual = null; lastContext = null; play(null); }, []);
}

// ══════════════════════════════════════════════════════════════
// <Soundtrack screen={screen} />
// ══════════════════════════════════════════════════════════════
// Mount ONCE, at the top of App. It renders nothing. It is a
// component rather than a hook so that the app has exactly one of
// it — two mounted hooks would each cue a track and you would hear
// both, which is the sort of thing that gets diagnosed as "the loop
// is doubled" for an hour before anybody counts the mounts.
export default function Soundtrack({ screen, enabled = true }) {
  useSoundtrack(screen, enabled);
  return null;
}

// ── For a screen that wants to duck the music briefly (a cutscene,
//    a reveal) without knowing anything about the engine. Returns the
//    level to whatever the current track asks for.
export function duck(amount = 0.25) {
  if (!current || !ctx) return () => {};
  const g = current.gain, base = TRACKS[current.id]?.gain ?? 0.4;
  try {
    g.gain.cancelScheduledValues(ctx.currentTime);
    g.gain.setTargetAtTime(base * amount, ctx.currentTime, 0.15);
  } catch { /* no-op */ }
  return () => {
    if (!current || !ctx) return;
    try {
      current.gain.gain.cancelScheduledValues(ctx.currentTime);
      current.gain.gain.setTargetAtTime(base, ctx.currentTime, 0.3);
    } catch { /* no-op */ }
  };
}

/** True once a track has actually decoded — for a settings row that
 *  should say "no music installed yet" rather than pretend. */
export const soundtrackReady = () => buffers.size > 0;
export const soundtrackMissing = () => dead.size > 0 && buffers.size === 0;

// ── THE MENU ROW ──────────────────────────────────────────────
// Terror has TWO menu owners — RoomOverlay builds the base sections
// for every wrapped room, and Plaza builds its own — so the sound
// controls are a FUNCTION both call rather than a block pasted into
// each. Two pasted copies is two copies that drift, and the drift
// shows up as "the mute in the Plaza and the mute in the Forge
// disagree," which is a bug nobody thinks to look for.
//
// Discrete leaves, not a slider: StartMenu's contract is that a leaf
// fires a callback and the menu closes. Volume CYCLES rather than
// stepping, so one row does the whole range and reopening the menu is
// not a punishment for wanting 50%.
// The labels read module state at BUILD time, not through the hook,
// and that is fine because StartMenu's contract closes the menu when a
// leaf fires — so the next time you can see the row, it has been
// rebuilt. Wiring useAudioSettings() into both menu owners to keep a
// label live behind a closed menu would be two subscriptions bought
// for nothing.
const VOLUME_STEPS = [0.3, 0.5, 0.7, 0.9, 1.0];

export function soundMenuSection() {
  const s = getAudioSettings();
  const pct = Math.round(s.volume * 100);
  return {
    id: "sound", label: "Sound", icon: "♪",
    items: [
      {
        id: "music_toggle",
        label: s.muted ? "Music: Off" : "Music: On",
        run: () => toggleMuted(),
      },
      {
        id: "music_volume",
        label: `Volume: ${pct}%`,
        run: () => {
          const i = VOLUME_STEPS.findIndex((v) => Math.abs(v - s.volume) < 0.01);
          setVolume(VOLUME_STEPS[(i + 1) % VOLUME_STEPS.length]);
        },
      },
    ],
  };
}
